import { useRoute, useNavigation, RouteProp } from "@react-navigation/native";
import type { StackNavigationProp } from '@react-navigation/stack';
import React, { useEffect, useMemo, useRef } from "react";

import {
  DEMO_CURRENT_QUESTION_INDEX,
  DEMO_QUIZ_QUESTIONS,
  getAllFormatSmokeQuestions,
  getSmokeQuizState,
  isScreenshotMode,
  type SmokeScenario,
} from "../../../lib/screenshotMode";
import { FreemiumQuizGate } from "../../features/monetization/components/FreemiumQuizGate";
import { logAnalyticsEvent } from "../../lib/firebase";
import { RootStackParamList } from "../../navigation/types";


import UnifiedQuizRenderer from "./UnifiedQuizRenderer";
import { useSinglePlayerQuizController } from "./useSinglePlayerQuizController";
import { summarizeFormatOutcomes } from '../services/achievementFormatStats';


const QuizGameScreen = () => {
  const navigation = useNavigation<StackNavigationProp<RootStackParamList, 'SinglePlayerGame'>>();
  const route = useRoute<RouteProp<RootStackParamList, 'SinglePlayerGame'>>();
  const screenshotMode = isScreenshotMode();
  const screenshotScenario = screenshotMode ? route.params?.smokeScenario : undefined;
  const isAutoLiveLoop = screenshotScenario === 'live_loop_auto';
  const isAllFormatLiveLoop = screenshotScenario === 'live_loop_all_formats';
  const initialSoloRevealStage = screenshotScenario === 'correct_verse' ? 'verse' : 'feedback';
  const smokeState = screenshotScenario && !isAutoLiveLoop && !isAllFormatLiveLoop
    ? getSmokeQuizState(screenshotScenario as SmokeScenario)
    : null;
  const category = route.params?.category ?? 'Gospels';
  const categoryId = route.params?.categoryId ?? 3;
  const questionCount = route.params?.questionCount ?? 10;
  const difficulty_id = route.params?.difficulty_id ?? 2;
  const userId = route.params?.userId;
  const screenshotInitialQuestions = useMemo(() => {
    if (!screenshotMode) return undefined;
    return isAllFormatLiveLoop ? getAllFormatSmokeQuestions() : DEMO_QUIZ_QUESTIONS;
  }, [isAllFormatLiveLoop, screenshotMode]);

  const {
    questions,
    currentQuestion,
    currentIndex,
    totalQuestions,
    answers,
    questionOutcomes,
    getElapsedSeconds,
    loading,
    score,
    correctCount,
    finished,
    error,
    handleAnswer,
    retryLoadQuestions,
    showFeedback,
    timeLeft,
    maxTime,
    handleNextQuestion,
  } = useSinglePlayerQuizController({
    category,
    categoryId,
    questionCount,
    difficulty_id,
    onQuizEnd: undefined,
    initialQuestions: screenshotInitialQuestions,
    sessionId: undefined,
  });

  // Top of the engagement funnel: first_open -> quiz_start -> quiz_complete -> paywall_view.
  // Fires once, when the questions are actually on screen (not when the screen mounts and
  // a slow network leaves the player staring at a spinner).
  const quizStartLoggedRef = useRef(false);
  useEffect(() => {
    if (screenshotMode || loading || error || quizStartLoggedRef.current || questions.length === 0) return;
    quizStartLoggedRef.current = true;
    void logAnalyticsEvent('quiz_start', {
      mode: 'solo',
      category_id: categoryId,
      difficulty_id,
      question_count: questions.length,
    });
  }, [screenshotMode, loading, error, questions.length, categoryId, difficulty_id]);

  // Screenshot mode: signal readiness after first render
  useEffect(() => {
    if (screenshotMode && typeof window !== 'undefined') {
      (window as Window & { __screenshotReady?: string }).__screenshotReady = '02_quiz';
    }
  }, [screenshotMode]);

  // Navigate to submitting/results screen when quiz finishes
  useEffect(() => {
    if (screenshotMode) return;
    if (finished) {
      const resultsData = {
        userId: userId ?? '',
        categoryId,
        difficulty_id,
        score: correctCount,
        correctCount,
        gameplayScore: score,
        totalQuestions,
        answers,
        // Recorded with the result so the achievement evaluator can count
        // True/False and multi-select mastery and the under-2-minute badge.
        formatStats: summarizeFormatOutcomes(questionOutcomes),
        timeTakenSeconds: getElapsedSeconds(),
        questions: questions.map(q => ({
          id: q.id,
          question_type: q.question_type,
          question: q.question,
          options: q.options,
          correct_answer: ('correct_answer' in q ? q.correct_answer : undefined) ?? q.answer ?? '',
          explanation: q.explanation ?? '',
        })),
      };
      navigation.replace("SinglePlayerSubmittingScreen", { resultsData });
    }
  }, [finished, userId, categoryId, difficulty_id, score, correctCount, totalQuestions, answers, questions, questionOutcomes, getElapsedSeconds, navigation, screenshotMode]);

  // In screenshot mode, render with demo data at a specific question index
  const useStaticSmokeState = screenshotMode && !isAutoLiveLoop && !isAllFormatLiveLoop;

  const effectiveQuestions = useStaticSmokeState
    ? (smokeState?.questions ?? DEMO_QUIZ_QUESTIONS)
    : questions;
  const effectiveIndex = useStaticSmokeState
    ? (smokeState?.currentIndex ?? DEMO_CURRENT_QUESTION_INDEX)
    : currentIndex;
  const effectiveQuestion = useStaticSmokeState
    ? (smokeState?.currentQuestion ?? DEMO_QUIZ_QUESTIONS[DEMO_CURRENT_QUESTION_INDEX])
    : currentQuestion;
  const effectiveTotal = useStaticSmokeState
    ? (smokeState?.totalQuestions ?? DEMO_QUIZ_QUESTIONS.length)
    : totalQuestions;
  const effectiveSelectedAnswer = useStaticSmokeState
    ? (smokeState?.selectedAnswer ?? null)
    : answers[currentIndex];
  const effectiveShowExplanation = useStaticSmokeState
    ? (smokeState?.showExplanation ?? false)
    : showFeedback;
  const effectiveScore = useStaticSmokeState
    ? (smokeState?.score ?? 2)
    : score;
  const effectiveTimeLeft = useStaticSmokeState
    ? (smokeState?.timeLeft ?? 25)
    : timeLeft;
  const effectiveMaxTime = useStaticSmokeState
    ? (smokeState?.maxTime ?? 30)
    : maxTime;
  const autoLoopQuestionRef = useRef<number | null>(null);
  const autoLoopTargetIndex = 4;

  useEffect(() => {
    if (!screenshotMode || (!isAutoLiveLoop && !isAllFormatLiveLoop) || loading || finished || !currentQuestion) {
      return;
    }

    if (currentIndex >= (isAllFormatLiveLoop ? 5 : autoLoopTargetIndex)) {
      return;
    }

    if (!showFeedback) {
      if (autoLoopQuestionRef.current === currentIndex) {
        return;
      }

      autoLoopQuestionRef.current = currentIndex;
      const correctAnswer = currentQuestion.correct_answer ?? currentQuestion.answer ?? null;
      const questionType = currentQuestion.question_type;
      const nextAnswer = questionType === 'mcq_multiple'
        ? { format: 'mcq_multiple' as const, answers: [1, 2, 4] }
        : questionType === 'true_false'
          ? { format: 'true_false' as const, answer: Boolean(correctAnswer) }
        : questionType === 'fill_blank'
          ? { format: 'fill_blank' as const, answers: { blank1: 'Lord', blank2: 'want' } }
          : questionType === 'ordering'
            ? { format: 'ordering' as const, order: [1, 2, 3, 4] }
            : correctAnswer;

      if (!nextAnswer) {
        return;
      }

      const answerTimer = setTimeout(() => {
        handleAnswer(nextAnswer);
      }, isAllFormatLiveLoop ? 1400 : 450);

      return () => clearTimeout(answerTimer);
    }

    if (isAllFormatLiveLoop) {
      return;
    }

    const nextTimer = setTimeout(() => {
      handleNextQuestion();
    }, 500);

    return () => clearTimeout(nextTimer);
  }, [
    screenshotMode,
    isAutoLiveLoop,
    isAllFormatLiveLoop,
    loading,
    finished,
    currentQuestion,
    currentIndex,
    showFeedback,
    handleAnswer,
    handleNextQuestion,
  ]);

  return (
    <FreemiumQuizGate
      onQuizComplete={() => {
        console.log('Quiz completed - usage recorded');
      }}
    >
      <UnifiedQuizRenderer
        mode="solo"
        isLoading={screenshotMode ? false : loading}
        isGameFinished={screenshotMode ? false : finished}
        error={screenshotMode ? null : error}
        questions={effectiveQuestions}
        currentQuestion={effectiveQuestion}
        selectedAnswer={effectiveSelectedAnswer}
        showExplanation={effectiveShowExplanation}
        onAnswerSelect={handleAnswer}
        currentIndex={effectiveIndex}
        totalQuestions={effectiveTotal}
        score={effectiveScore}
        onNextQuestion={handleNextQuestion}
        onRetryLoadQuestions={retryLoadQuestions}
        timeLeft={effectiveTimeLeft}
        maxTime={effectiveMaxTime}
        initialSoloRevealStage={initialSoloRevealStage}
      />
    </FreemiumQuizGate>
  );
};

export default QuizGameScreen;
